import React, { useEffect, useState } from 'react';
import { Badge, Button, Divider, Grid, Image } from '@mantine/core';
import PropTypes from 'prop-types';
import { Photo, X } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import CreationWizardButton from './CreationWizardButton';
import AppText from '../../common/AppText';
import StoreCreationStoreSelectModal from './StoreCreationStoreSelectModal';
import { ECOM_STORE_STATUS_LIST } from '../../../config/constants';
import StoreCreationStockContentSelection from './StoreCreationStockContentSelection';

const STORE_COPY_OPTIONS = [
  {
    label: 'Branding',
    description:
      'Copy all store branding settings including colors, logos, and banners.',
    value: 'copyBranding'
  },
  {
    label: 'Products',
    description:
      'Copy all products including custom products, collections, markups, and their relative settings.',
    value: 'copyProducts'
  },
  {
    label: 'Taxes',
    description:
      'Copy all store tax settings including tax rates and tax exemptions.',
    value: 'copyTaxes'
  },
  {
    label: 'Webstore',
    description: 'Copy all menus, themes, and other webstore settings.',
    value: 'copyWebstore'
  },
  {
    label: 'Media',
    description:
      'Copy all media files including logos, banners, and other images.',
    value: 'copyMedia'
  },
  {
    label: 'Fundraisers',
    description: 'Copy all fundraiser settings include player or team names.',
    value: 'copyFundraisers'
  },
  {
    label: 'Admins',
    description:
      'Assign the same administrators as the selected store to this new store.',
    value: 'copyAdmins'
  }
];

const StoreCreationAdminStockContentStep = ({
  isSportsHeadzAdmin,
  loading,
  createOptions,
  formState,
  setFormState
}) => {
  const [viewState, setViewState] = useState({
    isStoreModalOpen: false,
    showAllStockContent: false,
    search: '',
    pageIndex: 1
  });

  const selectedStoreStatusInfo = formState.copyContent.ecomStore
    ? ECOM_STORE_STATUS_LIST.find((opt) =>
        opt.isSelected(formState.copyContent.ecomStore)
      ) || { label: 'Unknown', color: 'grey' }
    : null;

  useEffect(() => {
    if (formState.copyContent.ecomStore) {
      if (formState.copyContent.copyBranding) {
        setFormState((c) => ({
          ...c,
          storeSettings: {
            ...c.storeSettings,
            supportEmail:
              c.copyContent.ecomStore?.supportEmail ||
              c.storeSettings.supportEmail ||
              '',
            primaryColor:
              c.copyContent.ecomStore?.primaryColor ||
              c.storeSettings.primaryColor ||
              '#000000',
            secondaryColor:
              c.copyContent.ecomStore?.secondaryColor ||
              c.storeSettings.secondaryColor ||
              '#FFFFFF',
            logoImageUrl:
              c.copyContent.ecomStore?.logoImageUrl ||
              c.storeSettings.logoImageUrl ||
              ''
          }
        }));
      }
      else {
        setFormState((c) => ({
          ...c,
          storeSettings: {
            ...c.storeSettings,
            // eslint-disable-next-line no-use-before-define
            ...getUnsetStoreBranding(c)
          }
        }));
      }
    }
  }, [formState.copyContent.ecomStore, formState.copyContent.copyBranding]);

  const getUnsetStoreBranding = (currentState) => {
    const regAssociation =
      createOptions.regAssociations.find(
        (a) => a.pkRegAssociation === currentState.team?.pkRegAssociation
      ) || null;

    const logoImage =
      currentState.storeSettings.logoImageUrl ===
      currentState.copyContent.ecomStore.logoImageUrl
        ? currentState.team?.image || regAssociation?.logoImageUrl || ''
        : currentState.storeSettings.logoImageUrl;

    return {
      supportEmail:
        currentState.storeSettings.supportEmail ===
        currentState.copyContent.ecomStore.supportEmail
          ? regAssociation?.supportEmail || ''
          : currentState.storeSettings.supportEmail,
      primaryColor:
        currentState.storeSettings.primaryColor ===
        currentState.copyContent.ecomStore.primaryColor
          ? currentState.team?.primaryColor ||
            regAssociation?.primaryColor ||
            '#000000'
          : currentState.storeSettings.primaryColor,
      secondaryColor:
        currentState.storeSettings.secondaryColor ===
        currentState.copyContent.ecomStore.secondaryColor
          ? currentState.team?.secondaryColor ||
            regAssociation?.secondaryColor ||
            '#FFFFFF'
          : currentState.storeSettings.secondaryColor,
      logoImageUrl: logoImage
    };
  };

  const onSelectStore = (ecomStore) => {
    setFormState({
      ...formState,
      ecomStockContent: [],
      copyContent: {
        ...formState.copyContent,
        ecomStore
      }
    });
    setViewState({ ...viewState, isStoreModalOpen: false });
  };

  const onUnselectStore = () => {
    setFormState((c) => ({
      ...formState,
      storeSettings: {
        ...formState.storeSettings,
        ...getUnsetStoreBranding(c)
      },
      copyContent: {
        ...formState.copyContent,
        ecomStore: null
      }
    }));
  };

  return (
    <AppStack style={{ gap: 10, paddingTop: 10 }}>
      {formState.copyContent.ecomStore ? (
        <AppStack style={{ gap: 10 }}>
          <AppText
            style={{
              textAlign: 'center',
              color: '#666',
              fontSize: 14,
              fontWeight: 'normal',
              fontStyle: 'italic'
            }}
          >
            Copying from{' '}
            <span style={{ fontWeight: 500 }}>
              {formState.copyContent.ecomStore.name}
            </span>
          </AppText>
          <AppStack style={{ gap: 5 }}>
            <AppFlexbox
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8
              }}
            >
              <AppText
                style={{
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Copy store
              </AppText>

              <Button
                color="dark"
                leftSection={<X size={18} />}
                onClick={onUnselectStore}
                size="compact-sm"
                variant="outline"
              >
                Unselect store
              </Button>
            </AppFlexbox>

            <AppCard radius={8} shadow="sm" style={{ padding: 0 }} withBorder>
              <AppFlexbox
                style={{
                  padding: '10px 16px',
                  gap: 16,
                  alignItems: 'center'
                }}
              >
                <AppStack
                  style={{
                    width: 40,
                    height: 40,
                    border: formState.copyContent.ecomStore.logoImageUrl
                      ? 'none'
                      : 'solid 1px #dee2e6',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {formState.copyContent.ecomStore.logoImageUrl ? (
                    <Image
                      h={40}
                      src={formState.copyContent.ecomStore.logoImageUrl}
                      w={40}
                    />
                  ) : (
                    <Photo color="#666" size={24} />
                  )}
                </AppStack>
                <AppStack style={{ gap: 3 }}>
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    {formState.copyContent.ecomStore.name}
                  </AppText>

                  <Badge color={selectedStoreStatusInfo.color} size="sm">
                    {selectedStoreStatusInfo.label}
                  </Badge>
                </AppStack>
              </AppFlexbox>
            </AppCard>
          </AppStack>

          <Divider
            label="Copy options"
            labelPosition="left"
            styles={{ label: { color: '#000', fontWeight: 500, fontSize: 14 } }}
          />

          <Grid>
            {STORE_COPY_OPTIONS.map((option) => (
              <Grid.Col key={option.value} span={12}>
                <CreationWizardButton
                  description={option.description}
                  label={option.label}
                  onClick={() => {
                    setFormState({
                      ...formState,
                      copyContent: {
                        ...formState.copyContent,
                        [option.value]: !formState.copyContent[option.value]
                      }
                    });
                  }}
                  selected={formState.copyContent[option.value] || false}
                />
              </Grid.Col>
            ))}
          </Grid>
        </AppStack>
      ) : (
        <>
          <StoreCreationStockContentSelection
            createOptions={createOptions}
            formState={formState}
            loading={loading}
            setFormState={setFormState}
            setViewState={setViewState}
            viewState={viewState}
          />

          {!formState.copyContent.ecomStore &&
            isSportsHeadzAdmin &&
            !viewState.showAllStockContent && (
              <AppStack style={{ gap: 10, marginTop: 20 }}>
                <Divider label="Admin options" labelPosition="left" />

                <AppStack style={{ gap: 0 }}>
                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontWeight: 500, fontSize: 14 }}>
                      Copy store content
                    </AppText>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      Would you like to copy an entire store instead?
                    </AppText>
                  </AppStack>
                </AppStack>

                <CreationWizardButton
                  description="Copy all products and settings from an existing store."
                  hideCheckbox
                  label="Select store to copy"
                  onClick={() => {
                    setViewState({
                      ...viewState,
                      isStoreModalOpen: true,
                      pageIndex: 1
                    });
                  }}
                />
              </AppStack>
            )}
        </>
      )}

      <StoreCreationStoreSelectModal
        isOpen={viewState.isStoreModalOpen}
        onClose={() => {
          setViewState({ ...viewState, isStoreModalOpen: false });
        }}
        onSelect={onSelectStore}
      />
    </AppStack>
  );
};

StoreCreationAdminStockContentStep.propTypes = {
  createOptions: PropTypes.object,
  formState: PropTypes.object,
  isSportsHeadzAdmin: PropTypes.bool,
  loading: PropTypes.bool,
  setFormState: PropTypes.func
};

export default StoreCreationAdminStockContentStep;
